import {forwardRef, type PropsWithChildren, useCallback, useImperativeHandle, useRef, useState} from "react";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {useAttachmentCollectionEditor} from "app/components/hooks/useAttachmentCollectionEditor";
import {AddEditOrgDoc} from "app/employee/organizer/modal/AddEditOrgDoc";
import type {OrganizerDocumentFragment, OrganizerFragment} from "app/gql/graphqlSchema";
import type {ID} from "app/utils/types";

export interface OrgDocUploaderHandle {
  showFileUpload(defaults?: Partial<{folderId: ID}>): void;
  editDocument(document: OrganizerDocumentFragment): Promise<void>;
}

interface Props {
  onCompleted?: () => void;
  organizer: OrganizerFragment;
}

export const OrgDocUploader = forwardRef<OrgDocUploaderHandle, PropsWithChildren<Props>>(
  ({onCompleted, organizer}, ref) => {
    const modal = useRef<HTMLIonModalElement>(null);
    const [editDocument, setEditDocument] = useState<OrganizerDocumentFragment | null>(null);
    const [defaults, setDefaults] = useState<Partial<{folderId: ID}> | null>(null);

    const {showFileUpload, unifiedAttachments, uploadProgress, updateAttachments, cleanup} =
      useAttachmentCollectionEditor(editDocument?.attachmentCollection, () => modal.current?.present());

    const dismissDialog = useCallback(async () => {
      cleanup();
      setEditDocument(null);
      setDefaults(null);

      await modal.current?.dismiss();
      onCompleted?.();
    }, [cleanup, onCompleted, setEditDocument]);

    useImperativeHandle(
      ref,
      () => ({
        showFileUpload: (defaults) => {
          setDefaults(defaults ?? null);
          showFileUpload();
        },
        editDocument: async (document) => {
          setEditDocument(document);
          setDefaults(null);
          await modal.current?.present();
        }
      }),
      [setEditDocument, showFileUpload]
    );

    return (
      <FstIonModal onIonModalDidDismiss={dismissDialog} ref={modal}>
        <AddEditOrgDoc
          defaults={defaults}
          document={editDocument}
          organizer={organizer}
          showFileUpload={showFileUpload}
          unifiedAttachments={unifiedAttachments}
          updateAttachments={updateAttachments}
          uploadProgress={uploadProgress}
        />
      </FstIonModal>
    );
  }
);

OrgDocUploader.displayName = "DocumentUploader";
